/**
 * TextSeriesReader.jsx — Feb 9 2026.
 *
 * Reader-facing page for a Text Series episode. Three consumption
 * modes, all driven by user choice:
 *
 *   1. PLAIN READ      — scroll the body in a wide serif column
 *   2. FLASH READ      — open the FlashReadOverlay (1/2/3-word chunks)
 *   3. LISTEN (TTS)    — stream audio via /api/tts/generate-long
 *
 * The TTS voice picker offers:
 *   • "Default" → free XTTS-grade voice, no balance deduction
 *   • Any HD voice → consumes voice minutes via /api/tts/voice-minutes/consume
 *
 * Insufficient minutes → 402 from /consume → we show a Buy Pack pill.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Volume2, Pause, Play, Square, Zap, ChevronRight, AlertCircle, CheckCircle2, Coins, Sparkles } from 'lucide-react';
import { authFetch } from '../utils/authUtils';
import { FlashReadButton } from '../components/FlashReadOverlay';
import EpisodeCountdownOverlay from '../components/EpisodeCountdownOverlay';
import RetentionQuizOverlay from '../components/RetentionQuizOverlay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Hard-coded voice catalogue — keep id/label/kind in sync with the
// existing /api/tts/voices endpoint. `kind:'free'` means default XTTS
// (no minutes deducted); `kind:'hd'` means it draws from the user's
// voice-minute balance.
const VOICES = [
  { id: 'default', label: 'Default (free)', kind: 'free' },
  { id: 'nova',    label: 'Nova (HD)',       kind: 'hd' },
  { id: 'onyx',    label: 'Onyx (HD)',       kind: 'hd' },
  { id: 'sage',    label: 'Sage (HD)',       kind: 'hd' },
];

const TextSeriesReader = () => {
  const { seriesId, episodeId } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [seriesEpisodes, setSeriesEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(null);

  // TTS player state
  const [voice, setVoice] = useState('default');
  const [audioUrl, setAudioUrl] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const [insufficient, setInsufficient] = useState(false);
  const audioRef = useRef(null);

  // Mark-as-read state — Feb 12 2026 user spec: "Assign xp to book
  // series". POSTs to /api/text-series/.../finish which deduplicates
  // per (user, episode) and awards XP + coins on the first call.
  const [finished, setFinished] = useState(null); // {xp_awarded, coins_awarded, already_finished}
  const [finishing, setFinishing] = useState(false);

  // Feb 12 2026 — auto-advance countdown for flash/chunk/TTS endings.
  // The next-episode lookup walks the series' episode list (ordered by
  // episode_number, loaded via /text-series/{id}) and picks the one
  // immediately after the current episodeId. Returns null when we're
  // on the last episode.
  const [showCountdown, setShowCountdown] = useState(false);
  // Optional retention quiz — opens automatically after every fresh
  // episode finish (the overlay checks the user's toggle + first-time
  // prompt state internally).
  const [showQuiz, setShowQuiz] = useState(false);
  const nextEpisode = (() => {
    const eps = seriesEpisodes || [];
    if (!eps.length) return null;
    const idx = eps.findIndex((e) => (e.episode_id === episodeId));
    if (idx < 0 || idx >= eps.length - 1) return null;
    return eps[idx + 1];
  })();
  // Iter 82 — symmetric prev-episode lookup so the reader can rewind
  // a chapter without going back to the series-detail page.
  const prevEpisode = (() => {
    const eps = seriesEpisodes || [];
    if (!eps.length) return null;
    const idx = eps.findIndex((e) => (e.episode_id === episodeId));
    if (idx <= 0) return null;
    return eps[idx - 1];
  })();
  const goNextEpisode = () => {
    if (!nextEpisode) return;
    setShowCountdown(false);
    navigate(`/text-series/${seriesId}/episode/${nextEpisode.episode_id}`);
  };
  const goPrevEpisode = () => {
    if (!prevEpisode) return;
    navigate(`/text-series/${seriesId}/episode/${prevEpisode.episode_id}`);
  };

  const markFinished = async () => {
    if (finishing) return;
    setFinishing(true);
    try {
      const r = await authFetch(
        `${BACKEND_URL}/api/text-series/${seriesId}/episodes/${episodeId}/finish`,
        { method: 'POST' },
      );
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(d.detail || 'Could not mark as read');
        return;
      }
      setFinished(d);
      if (!d.already_finished && (d.xp_awarded || d.coins_awarded)) {
        toast.success(`+${d.xp_awarded} XP${d.coins_awarded ? ` · +${d.coins_awarded} coins` : ''}`);
      } else if (d.already_finished) {
        toast.info('Already credited');
      }
      // Feb 12 2026 — retention quiz fires after every Book Series
      // episode finish (the overlay itself decides whether to show the
      // first-time prompt, run the quiz, or stay closed based on the
      // user's toggle setting).
      if (!d.already_finished) setShowQuiz(true);
    } catch {
      toast.error('Network error');
    } finally {
      setFinishing(false);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await authFetch(`${BACKEND_URL}/api/text-series/${seriesId}/episodes/${episodeId}`);
        if (!r.ok) {
          if (r.status === 403) toast.error('Vault series — not unlocked');
          else if (r.status === 404) toast.error('Episode not found');
          return;
        }
        setData(await r.json());
        // Pull the full ordered episode list separately — the single
        // episode endpoint doesn't include sibling metadata, so we hit
        // /text-series/{id} to know which episode comes next.
        try {
          const er = await authFetch(`${BACKEND_URL}/api/text-series/${seriesId}`);
          if (er.ok) {
            const ed = await er.json();
            setSeriesEpisodes(ed.episodes || []);
          }
        } catch { /* sibling list is best-effort */ }
        // Refresh voice-minutes balance for the HUD.
        try {
          const b = await authFetch(`${BACKEND_URL}/api/tts/voice-minutes`).then((x) => x.json());
          setBalance(b);
        } catch { /* signed-out viewers don't have a balance */ }
      } finally { setLoading(false); }
    })();
  }, [seriesId, episodeId]);

  // Build the synthesis payload only when text exists.
  const body = data?.episode?.body_text || '';

  // Estimated audio duration in seconds for the HD voice deduction
  // calculation. ~150 wpm is the universal narrator average; we use it
  // to pre-flight the minutes check before generating.
  const estSecs = useMemo(() => {
    const w = (body || '').split(/\s+/).filter(Boolean).length;
    return Math.max(0, Math.ceil((w / 150) * 60));
  }, [body]);

  const minutesNeeded = Math.ceil(estSecs / 60);
  const isHd = VOICES.find((v) => v.id === voice)?.kind === 'hd';
  const balanceMins = balance?.balance_minutes ?? 0;
  const canPlayHd = !isHd || balanceMins >= minutesNeeded;

  const generateTTS = async () => {
    if (!body) return;
    if (isHd && !canPlayHd) {
      setInsufficient(true);
      return;
    }
    setInsufficient(false);
    setGenerating(true);
    setAudioUrl(null);
    try {
      // Generate the audio first — the existing TTS endpoint accepts
      // the scribe's voice param. The response is an audio job URL.
      const r = await authFetch(`${BACKEND_URL}/api/tts/generate-long`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: body,
          voice_id: voice,
          ref_kind: 'text_series_episode',
          ref_id: episodeId,
        }),
      });
      if (!r.ok) {
        toast.error('Could not generate audio');
        return;
      }
      const d = await r.json();
      const url = d.audio_url || d.url || d.public_url || d.download_url;
      if (!url) { toast.error('No audio URL returned'); return; }
      setAudioUrl(url.startsWith('http') ? url : `${BACKEND_URL}${url}`);

      // If HD voice, deduct minutes server-side now that we know the
      // synthesis succeeded. We use the estimated duration; the backend
      // floors at the actual server-side measured length if available.
      if (isHd) {
        const cr = await authFetch(`${BACKEND_URL}/api/tts/voice-minutes/consume`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ seconds: estSecs, voice_id: voice, ref: episodeId }),
        });
        if (cr.status === 402) {
          setInsufficient(true);
          toast.error('Voice minutes depleted — switch to Default or buy more');
        } else if (cr.ok) {
          const cd = await cr.json();
          setBalance((b) => b ? { ...b, balance_minutes: cd.balance_minutes } : b);
        }
      }
    } finally {
      setGenerating(false);
    }
  };

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) { a.play(); setPlaying(true); }
    else { a.pause(); setPlaying(false); }
  };

  const stopAudio = () => {
    const a = audioRef.current;
    if (!a) return;
    a.pause();
    a.currentTime = 0;
    setPlaying(false);
    setCurrent(0);
  };

  if (loading) {
    return (
      <main className="sharp-bg min-h-screen px-4 py-6 max-w-3xl mx-auto">
        <p className="text-white/40 text-sm">Loading episode…</p>
      </main>
    );
  }
  if (!data) {
    return (
      <main className="sharp-bg min-h-screen px-4 py-6 max-w-3xl mx-auto">
        <p className="text-rose-300 text-sm">Episode unavailable.</p>
        <button onClick={() => navigate(-1)} className="mt-3 text-amber-300 underline">Back</button>
      </main>
    );
  }

  const { series, episode } = data;

  return (
    <main className="sharp-bg min-h-screen max-w-3xl mx-auto px-4 py-6 pb-32" data-testid="text-series-reader-page">
      <button
        type="button"
        onClick={() => navigate(`/text-series/${series.series_id}`)}
        className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-white/60 hover:text-white mb-5"
        data-testid="text-series-reader-back"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> {series.title}
      </button>

      <header className="mb-5 flex items-start gap-3">
        {/* Iter 82 — Previous Ep pill (only when a previous exists). */}
        {prevEpisode && (
          <button
            type="button"
            onClick={goPrevEpisode}
            className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-full bg-white/[0.06] hover:bg-white/[0.12] border border-white/15 text-white/80 px-3 py-2 text-[11px] font-black uppercase tracking-widest transition-colors mt-6"
            data-testid="text-series-reader-prev-episode"
            title={`Previous: ${prevEpisode.title}`}
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Prev
          </button>
        )}
        <div className="flex-1 min-w-0">
          <p className="eyebrow text-indigo-300">Episode {episode.episode_number}</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black text-white tracking-tight mt-1">
            {episode.title}<span className="text-indigo-300">.</span>
          </h1>
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/45 mt-1 tabular-nums">
            {episode.word_count} words · ~{Math.max(1, Math.ceil(episode.word_count / 250))} min read
          </p>
        </div>
        {/* Iter 82 — persistent Next episode nav in the header. User asked
            for a way to reach the next ep without scrolling to the auto-
            advance pill at the footer. Only shown when there IS a next. */}
        {nextEpisode && (
          <button
            type="button"
            onClick={goNextEpisode}
            className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-full bg-indigo-500/15 hover:bg-indigo-500/25 border border-indigo-400/40 text-indigo-200 px-3 py-2 text-[11px] font-black uppercase tracking-widest transition-colors mt-6"
            data-testid="text-series-reader-next-episode"
            title={`Next: ${nextEpisode.title}`}
          >
            Next ep
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        )}
      </header>

      {/* Reader controls — Flash + TTS */}
      <section
        className="rounded-2xl bg-[#12131A] border border-white/10 p-4 mb-6"
        data-testid="text-series-reader-controls"
      >
        <p className="eyebrow text-amber-300 mb-3">How would you like to read?</p>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <FlashReadButton
            text={body}
            title={episode.title}
            testid="text-series-flash-btn"
            label="Flash read"
            className="text-xs"
            onNext={nextEpisode ? goNextEpisode : null}
            nextEpisodeTitle={nextEpisode?.title || ''}
          />
          {/* Voice picker as pill row */}
          <div className="flex items-center gap-1.5" data-testid="text-series-voice-picker">
            {VOICES.map((v) => {
              const active = voice === v.id;
              const isHdV = v.kind === 'hd';
              return (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => { setVoice(v.id); setAudioUrl(null); setInsufficient(false); }}
                  className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-[0.2em] border transition ${
                    active
                      ? (isHdV ? 'border-fuchsia-400 bg-fuchsia-400/15 text-fuchsia-300'
                              : 'border-emerald-400 bg-emerald-400/15 text-emerald-300')
                      : 'border-white/10 text-white/55 hover:border-white/30'
                  }`}
                  data-testid={`text-series-voice-${v.id}`}
                >
                  {v.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* HD voice minute badge */}
        {isHd && (
          <div className="text-[11px] text-white/55 mb-3 inline-flex items-center gap-1.5 flex-wrap">
            <Volume2 className="w-3.5 h-3.5 text-fuchsia-300" />
            HD voice consumes <strong className="text-fuchsia-300 tabular-nums">~{minutesNeeded} min</strong> from your balance{' '}
            (<strong className={`tabular-nums ${canPlayHd ? 'text-emerald-300' : 'text-rose-300'}`}>{balanceMins} min</strong> available)
          </div>
        )}
        {insufficient && (
          <div
            className="rounded-xl border border-rose-400/40 bg-rose-500/10 px-3 py-2 mb-3 flex items-start gap-2"
            data-testid="text-series-insufficient-banner"
          >
            <AlertCircle className="w-4 h-4 text-rose-300 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-[11px] text-rose-200">
              You&apos;re out of HD voice minutes for this episode. Switch to <strong>Default (free)</strong> or top up.
              <button
                onClick={() => navigate('/voice-minutes')}
                className="ml-2 underline text-rose-300"
                data-testid="text-series-buy-minutes-cta"
              >
                Buy minutes →
              </button>
            </div>
          </div>
        )}

        {/* Generate / Player */}
        {!audioUrl ? (
          <button
            type="button"
            onClick={generateTTS}
            disabled={generating || (isHd && !canPlayHd)}
            className={`w-full sm:w-auto px-5 py-2.5 rounded-full font-black uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2 transition ${
              isHd
                ? 'bg-fuchsia-400 hover:bg-fuchsia-300 text-black'
                : 'bg-emerald-400 hover:bg-emerald-300 text-black'
            } disabled:opacity-60`}
            data-testid="text-series-tts-generate"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Volume2 className="w-4 h-4" />}
            {generating ? 'Generating audio…' : `Listen with ${VOICES.find(v => v.id === voice)?.label}`}
          </button>
        ) : (
          <div className="flex items-center gap-2" data-testid="text-series-tts-player">
            <button
              type="button"
              onClick={togglePlay}
              className="w-11 h-11 rounded-full bg-amber-400 hover:bg-amber-300 text-black inline-flex items-center justify-center"
              data-testid="text-series-tts-toggle"
            >
              {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            </button>
            <button
              type="button"
              onClick={stopAudio}
              className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 inline-flex items-center justify-center"
              data-testid="text-series-tts-stop"
            >
              <Square className="w-4 h-4" />
            </button>
            <div className="flex-1 min-w-0">
              <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
                <div
                  className="h-full bg-amber-400 transition-[width]"
                  style={{ width: duration ? `${(current / duration) * 100}%` : '0%' }}
                />
              </div>
              <p className="text-[10px] text-white/40 mt-1 tabular-nums">
                {Math.floor(current)}s / {Math.floor(duration)}s · {VOICES.find(v => v.id === voice)?.label}
              </p>
            </div>
            <audio
              ref={audioRef}
              src={audioUrl}
              onLoadedMetadata={(e) => setDuration(e.target.duration || 0)}
              onTimeUpdate={(e) => setCurrent(e.target.currentTime || 0)}
              onEnded={() => {
                setPlaying(false);
                setCurrent(0);
                // Feb 12 2026 — auto-advance via 3-2-1 countdown when
                // there's a next episode. User can cancel via the ×.
                if (nextEpisode) setShowCountdown(true);
              }}
              className="hidden"
              data-testid="text-series-tts-audio"
            />
          </div>
        )}
      </section>

      {/* Body */}
      <article
        className="prose prose-invert max-w-none text-white/90"
        style={{ fontFamily: '"Fraunces", Georgia, serif' }}
        data-testid="text-series-body"
      >
        {body.split(/\n\s*\n/).map((para, i) => (
          <p key={i} className="text-base sm:text-lg leading-relaxed mb-4 whitespace-pre-wrap">
            {para}
          </p>
        ))}
      </article>

      {/* Bottom-of-episode Prev/Next nav — mirrors the header pills so
          readers who reach the end of a chapter don't have to scroll
          back up to advance. Feb 2026 user request. */}
      {(prevEpisode || nextEpisode) && (
        <nav
          className="mt-8 flex items-stretch gap-3"
          data-testid="text-series-reader-footer-nav"
          aria-label="Episode navigation"
        >
          {prevEpisode ? (
            <button
              type="button"
              onClick={goPrevEpisode}
              className="flex-1 min-w-0 rounded-2xl border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] transition-colors px-4 py-3 text-left"
              data-testid="text-series-reader-prev-episode-bottom"
              title={`Previous: ${prevEpisode.title}`}
            >
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/50 flex items-center gap-1">
                <ArrowLeft className="w-3 h-3" /> Previous episode
              </p>
              <p className="text-sm sm:text-base font-bold text-white/90 truncate mt-0.5">
                {prevEpisode.title}
              </p>
            </button>
          ) : (
            <div className="flex-1" aria-hidden="true" />
          )}
          {nextEpisode ? (
            <button
              type="button"
              onClick={goNextEpisode}
              className="flex-1 min-w-0 rounded-2xl border border-indigo-400/40 bg-indigo-500/15 hover:bg-indigo-500/25 transition-colors px-4 py-3 text-right"
              data-testid="text-series-reader-next-episode-bottom"
              title={`Next: ${nextEpisode.title}`}
            >
              <p className="text-[10px] uppercase tracking-[0.2em] text-indigo-200 flex items-center justify-end gap-1">
                Next episode <ChevronRight className="w-3 h-3" />
              </p>
              <p className="text-sm sm:text-base font-bold text-white truncate mt-0.5">
                {nextEpisode.title}
              </p>
            </button>
          ) : (
            <div className="flex-1" aria-hidden="true" />
          )}
        </nav>
      )}

      {/* Mark as read — XP + coin reward (Feb 12 2026). Only renders
          when the episode has body text and the user is signed in
          (anonymous viewers see a sign-in CTA instead). */}
      {body && (
        <section
          className="mt-8 rounded-2xl border border-amber-400/30 bg-amber-400/5 p-5 text-center space-y-3"
          data-testid="text-series-finish-card"
        >
          {finished ? (
            <div className="space-y-2" data-testid="text-series-finish-reward">
              <CheckCircle2 className="w-9 h-9 text-emerald-300 mx-auto" />
              <h3 className="text-lg font-black text-white">
                {finished.already_finished ? 'Already credited' : 'Episode complete'}
              </h3>
              <div className="flex items-center justify-center gap-3 text-sm">
                <span className="inline-flex items-center gap-1 text-amber-200 font-bold" data-testid="text-series-xp-awarded">
                  <Sparkles className="w-4 h-4" /> +{finished.xp_awarded || 0} XP
                </span>
                {(finished.coins_awarded || 0) > 0 && (
                  <span className="inline-flex items-center gap-1 text-yellow-200 font-bold" data-testid="text-series-coins-awarded">
                    <Coins className="w-4 h-4" /> +{finished.coins_awarded} coins
                  </span>
                )}
              </div>
              {/* Feb 12 2026 — coins are now pending until comprehension
                  test is passed. Show a CTA when there are pending coins
                  to claim. */}
              {(finished.coins_pending || 0) > 0 && !finished.coins_awarded && (
                <div
                  className="mt-3 rounded-xl border border-yellow-400/30 bg-yellow-400/5 p-3 text-left"
                  data-testid="text-series-coins-pending"
                >
                  <p className="text-xs text-yellow-200/90 font-bold uppercase tracking-widest mb-1">
                    Coins pending · {finished.coins_pending}
                  </p>
                  <p className="text-[11px] text-white/60 mb-2">
                    Pass a quick comprehension test (60%+) to claim your coin reward.
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate(`/quiz-demo?ref=${seriesId}/${episodeId}&claim=1`)}
                    className="inline-flex items-center gap-2 rounded-full bg-yellow-400 hover:bg-yellow-300 text-black text-xs font-black px-4 py-1.5 uppercase tracking-widest"
                    data-testid="text-series-claim-coins-btn"
                  >
                    <Coins className="w-3 h-3" /> Test comprehension · claim
                  </button>
                </div>
              )}
              {data?.episode?.word_count > 0 && (
                <p className="text-[11px] text-white/50">
                  {data.episode.word_count.toLocaleString()} words read
                </p>
              )}
              {/* Feb 12 2026 — auto-advance to next episode pill */}
              {nextEpisode && !showCountdown && (
                <button
                  type="button"
                  onClick={() => setShowCountdown(true)}
                  className="mt-3 inline-flex items-center gap-2 rounded-full border border-amber-400/60 bg-amber-400/10 hover:bg-amber-400/20 text-amber-200 text-xs font-black px-4 py-1.5 uppercase tracking-widest"
                  data-testid="text-series-auto-advance-btn"
                >
                  Next episode · <span className="truncate max-w-[160px] normal-case font-bold tracking-normal">{nextEpisode.title}</span> <ChevronRight className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ) : (
            <>
              <h3 className="text-lg font-black text-white">Finished this episode?</h3>
              <p className="text-xs text-white/60">
                Mark it read to claim your XP and coin reward
                {data?.episode?.word_count > 0 && (
                  <> · ~{Math.max(5, Math.min(50, Math.floor(data.episode.word_count / 150)))} XP</>
                )}
              </p>
              <button
                type="button"
                onClick={markFinished}
                disabled={finishing}
                className="inline-flex items-center gap-2 rounded-full bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-black font-black px-6 py-2.5 text-sm uppercase tracking-widest"
                data-testid="text-series-mark-read-btn"
              >
                {finishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Mark as read
              </button>
            </>
          )}
        </section>
      )}

      {/* Feb 12 2026 — full-screen 3-2-1 countdown for auto-advance to
          the next episode (Flash, chunk, TTS, and manual mark-as-read
          paths all funnel through here so the UX is identical). */}
      <EpisodeCountdownOverlay
        open={showCountdown}
        nextEpisodeTitle={nextEpisode?.title || ''}
        onComplete={goNextEpisode}
        onCancel={() => setShowCountdown(false)}
      />

      {/* Feb 12 2026 — optional retention quiz (3 questions, +2 coins
          per correct). Decides itself whether to render the first-time
          prompt, the quiz, or close instantly based on the user's
          toggle. */}
      <RetentionQuizOverlay
        open={showQuiz}
        seriesId={seriesId}
        episodeId={episodeId}
        onClose={() => setShowQuiz(false)}
      />
    </main>
  );
};

export default TextSeriesReader;
